// src/hooks/useTutorial.js
import { useState, useEffect } from "react";
import {
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  introDialogue,
  dashboardTutorial,
} from "./tutorialSequenceSteps";
import {
  finalizeTutorialOnboarding,
  LEGACY_TUTORIAL_SEQUENCES,
} from "./tutorialCompletion";

/**
 * Hooks into users/{uid}.tutorialProgress and tutorialSequences/{sequenceId}.
 */
export function useTutorial(uid) {
  const [progress, setProgress] = useState(null);
  const [sequence, setSequence] = useState(null);

  // 1) Listen to the user doc and seed tutorialProgress if missing
  useEffect(() => {
    if (!uid) return;
    const userRef = doc(db, "users", uid);

    const unsubUser = onSnapshot(userRef, async (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();

      // If no tutorialProgress yet, initialize it
      if (!data.tutorialProgress) {
        await updateDoc(userRef, {
          tutorialProgress: {
            currentSequence: "introDialogue",
            currentStepIndex: 0,
            completedSequences: [],
            updatedAt: serverTimestamp(),
          },
        });
        return; // wait for the next snapshot with the seeded data
      }

      const currentSequence = data.tutorialProgress?.currentSequence;
      if (LEGACY_TUTORIAL_SEQUENCES.has(currentSequence)) {
        await finalizeTutorialOnboarding(uid, {
          completedSequences: [
            ...(data.tutorialProgress?.completedSequences || []),
            "dashboardTutorial",
          ],
        });
        return;
      }

      setProgress(data.tutorialProgress);
    });

    return unsubUser;
  }, [uid]);

  // 2) Once we know the currentSequence, listen to that sequence doc
  useEffect(() => {
    if (!progress?.currentSequence) return;
    if (progress.currentSequence === "introDialogue") {
      setSequence(introDialogue);
      return;
    }
    if (progress.currentSequence === "dashboardTutorial") {
      setSequence(dashboardTutorial);
      return;
    }
    setSequence(null);
  }, [progress?.currentSequence]);

  // 3) Advance either to the next step or wrap up the sequence
  const advanceStep = async () => {
    if (!progress || !sequence) return;
    console.log("advanceStep", progress, sequence);
    const userRef = doc(db, "users", uid);
    const {
      currentSequence,
      currentStepIndex,
      completedSequences = [],
    } = progress;
    const nextCompleted = Array.from(
      new Set([...(completedSequences || []), currentSequence].filter(Boolean))
    );

    if (sequence.steps && currentStepIndex + 1 < sequence.steps.length) {
      // Move to next step in the same sequence
      await updateDoc(userRef, {
        "tutorialProgress.currentStepIndex": currentStepIndex + 1,
        "tutorialProgress.updatedAt": serverTimestamp(),
      });
    } else if (sequence.nextSequence) {
      // Finish this sequence, jump to the next one
      await updateDoc(userRef, {
        "tutorialProgress.currentSequence": sequence.nextSequence,
        "tutorialProgress.currentStepIndex": 0,
        "tutorialProgress.completedSequences": nextCompleted,
        "tutorialProgress.updatedAt": serverTimestamp(),
      });
    } else {
      // Last sequence complete
      await finalizeTutorialOnboarding(uid, {
        completedSequences: nextCompleted,
      });
    }
  };

  return { progress, sequence, advanceStep };
}
